<template>
  <div ref="box" style="position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:9999"></div>
</template>

<script>
// 模块级：用 Set 追踪当前被鼠标悬停的元素，Set 引用稳定不受 HMR 影响
const _paused = new Set()
let _uid = 0

export default {
  name: 'FloatingImages',
  props: {
    imageList: { type: Array, default: () => [] },
    count: { type: Number, default: 1 },
    minSize: { type: Number, default: 400 },
    maxSize: { type: Number, default: 400 },
    minSpeed: { type: Number, default: 0.5 },
    maxSpeed: { type: Number, default: 2 },
  },
  created() { this._items = [] },
  mounted() {
    this._build()
    this._raf = requestAnimationFrame(this._tick)
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    cancelAnimationFrame(this._raf)
    this._items.forEach(a => {
      a.el.remove()
      _paused.delete(a.el)
    })
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    _build() {
      const urls = this.imageList.length
        ? this.imageList
        : Array.from({ length: this.count }, (_, i) => `https://picsum.photos/200/200?random=${i}`)
      const W = window.innerWidth, H = window.innerHeight
      const box = this.$refs.box

      urls.forEach(src => {
        // 先加载图片获取原始比例，再创建 DOM
        const tmp = new Image()
        tmp.onload = () => {
          const natW = tmp.naturalWidth || 1, natH = tmp.naturalHeight || 1
          // 按随机尺寸缩放，保持原始比例
          const baseSz = this.minSize + Math.random() * (this.maxSize - this.minSize)
          const scale = baseSz / Math.max(natW, natH)
          const dw = Math.round(natW * scale)
          const dh = Math.round(natH * scale)
          this._createItem(box, src, dw, dh, W, H)
        }
        tmp.onerror = () => {
          // 加载失败时用正方形兜底
          const sz = this.minSize + Math.random() * (this.maxSize - this.minSize)
          this._createItem(box, src, sz, sz, W, H)
        }
        tmp.src = src
      })
    },
    _createItem(box, src, dw, dh, W, H) {
      const x = Math.random() * (W - dw)
      const y = Math.random() * (H - dh)
      const spd = this.minSpeed + Math.random() * (this.maxSpeed - this.minSpeed)

      // 创建容器
      const el = document.createElement('div')
      el.style.cssText = `position:absolute;width:${dw}px;height:${dh}px;left:${x}px;top:${y}px;cursor:pointer;pointer-events:auto`

      // 创建图片 - pointer-events:none 让鼠标事件穿透到 el
      const img = document.createElement('img')
      img.src = src
      img.style.cssText = `width:${dw}px;height:${dh}px;display:block;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.2);user-select:none;pointer-events:none`
      img.draggable = false

      // 创建关闭按钮
      const btn = document.createElement('span')
      btn.innerHTML = '&times;'
      btn.style.cssText = 'position:absolute;top:-12px;right:-12px;width:24px;height:24px;background:rgba(255,59,48,0.9);color:#fff;border-radius:50%;display:none;align-items:center;justify-content:center;font-size:16px;cursor:pointer;user-select:none;box-shadow:0 2px 6px rgba(0,0,0,0.3);z-index:1;pointer-events:auto'
      btn.addEventListener('click', e => { e.stopPropagation(); this._remove(item) })

      el.appendChild(img)
      el.appendChild(btn)
      box.appendChild(el)

      // mouseenter / mouseleave：鼠标进入/离开 el 边界时触发
      el.addEventListener('mouseenter', () => {
        _paused.add(el)
        if (!item.hitEdge) btn.style.display = 'flex'
      })
      el.addEventListener('mouseleave', e => {
        _paused.delete(el)
        if (e.relatedTarget === btn) return
        if (!item.hitEdge) btn.style.display = 'none'
      })
      btn.addEventListener('mouseenter', () => {
        _paused.add(el)
        if (!item.hitEdge) btn.style.display = 'flex'
      })
      btn.addEventListener('mouseleave', () => {
        _paused.delete(el)
        if (!item.hitEdge) btn.style.display = 'none'
      })

      const item = {
        id: _uid++, el, btn,
        x, y, w: dw, h: dh,
        vx: Math.random() > 0.5 ? spd : -spd,
        vy: Math.random() > 0.5 ? spd : -spd,
        hitEdge: false,
      }
      this._items.push(item)
    },
    _tick() {
      const W = window.innerWidth, H = window.innerHeight
      for (const a of this._items) {
        // 如果鼠标在此元素内，跳过移动（但保留碰边状态）
        if (_paused.has(a.el)) continue

        a.x += a.vx; a.y += a.vy

        // 反弹
        if (a.x <= 0) { a.x = 0; a.vx = Math.abs(a.vx) }
        else if (a.x + a.w >= W) { a.x = W - a.w; a.vx = -Math.abs(a.vx) }
        if (a.y <= 0) { a.y = 0; a.vy = Math.abs(a.vy) }
        else if (a.y + a.h >= H) { a.y = H - a.h; a.vy = -Math.abs(a.vy) }

        // 判断是否正在接触边缘（每帧重新计算，离开边缘后自动恢复）
        const atEdge = a.x <= 0 || a.x + a.w >= W || a.y <= 0 || a.y + a.h >= H
        if (atEdge !== a.hitEdge) {
          a.hitEdge = atEdge
          if (atEdge) a.btn.style.display = 'none'
        }

        a.el.style.left = a.x + 'px'
        a.el.style.top = a.y + 'px'
      }
      this._raf = requestAnimationFrame(this._tick)
    },
    _remove(item) {
      _paused.delete(item.el)
      item.el.remove()
      const idx = this._items.indexOf(item)
      if (idx !== -1) this._items.splice(idx, 1)
      this.$emit('remove', idx)
    },
    _onResize() {
      const W = window.innerWidth, H = window.innerHeight
      for (const a of this._items) {
        if (a.x + a.w > W) a.x = W - a.w
        if (a.y + a.h > H) a.y = H - a.h
        if (a.x < 0) a.x = 0
        if (a.y < 0) a.y = 0
        a.el.style.left = a.x + 'px'
        a.el.style.top = a.y + 'px'
      }
    },
  },
}
</script>
